import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons/faExclamationCircle";
import { faShieldQuartered } from "@fortawesome/pro-solid-svg-icons/faShieldQuartered";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Stack, Tab, Typography } from "@mui/material";
import { MprcBarTabs, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcMessageBox, MprcRequestErrorDialog, MprcRequestIndicator, MprcText, MprcTextMuted } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { MprcFilterBox, MprcFilterLine } from "components/filter";
import { IconButtonClose } from "components/iconButtons";
import { IconButtonExpandScreen } from "components/iconButtons/IconButtonExpandScreen";
import { IconAdd } from "components/icons/IconAdd";
import { MprcDropdownItem, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcSwitch } from "components/MprcSwitch";
import { GroupInfo, ManagedTopicChallengeRequest, MemberListDtoPaginatedList, TopicSelectorMode, UserItemDtoPaginatedList } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditTopicChallengeQuestions } from "views/assessment/topicChallenge/Home/EditTopicChallenge/EditTopicChallengeQuestions";
import { AudienceSelectorDropdown } from "views/foundation/group/components/AudienceSelectorDropdown";
import { ListMemberDrawer } from "views/foundation/member/drawers/ListMemberDrawer";
import { TopicSelectorHierarchicalDrawer } from "views/foundation/topic/drawers/TopicSelectorHierarchicalDrawer";
import { ListUsersDrawer } from "views/foundation/user/drawers/ListUsersDrawer";

////////////////////////////////////////////////////////////////////////////////

type AudienceOption = "cloneAudience" | "viewAll";

type AudienceItemOption = "edit" | "viewMembers" | "delete";

type TopicChallengeTabOptions = "questions" | "audience" | "options";

interface IPros {
    topicId: string;
    onClose: (success: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////
/// FUNCTIONS
////////////////////////////////////////////////////////////////////////////

export const EditTopicChallengeDialog: React.FC<IPros> = (props) => {
    const { topicId, onClose } = props;
    const api = useCoreApi();
    const { t } = useTranslation("assessment");
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [viewData, setViewData] = useState<ManagedTopicChallengeRequest>();
    const [loadDto, setLoadDto] = useState<ManagedTopicChallengeRequest>();
    const [currentTab, setCurrentTab] = useState<TopicChallengeTabOptions>("questions");
    const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
    const [totalQuestions, setTotalQuestions] = useState<number>(0);
    const [questionsChanged, setQuestionsChanged] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [viewError, setViewError] = useState<any>();

    ////////////////////////////////////////////////////////////////////////////

    const canSave = questionsChanged || !LibUtils.deepCompare(viewData, loadDto);
    const showChangedImpact = viewData?.isKnowledgeProveDirty == true || canSave;

    ///
    const updateRecord = (name: keyof ManagedTopicChallengeRequest, v: any) => {
        const newViewData = { ...viewData!, [name]: v };
        setViewData(newViewData);
    };

    ///
    const addGroup = (data?: GroupInfo | null) => {
        if (data) {
            const newViewData = { ...viewData!, groups: [...viewData!.groups, data] };
            setViewData(newViewData);
        }
    };

    ///
    const getMenuItems = useMemo((): MprcDropdownItem<AudienceOption>[] => {
        const items: MprcDropdownItems<AudienceOption> = [
            { text: t("TopicChallenge.Edit.CloneAudienceAction"), value: "cloneAudience" },
            { text: t("TopicChallenge.Edit.ViewAllAction"), value: "viewAll" }
        ];
        return items;
    }, [viewData]);

    ///
    const getItemMenuOptions = useMemo((): MprcDropdownItem<AudienceItemOption>[] => {
        const items: MprcDropdownItems<AudienceItemOption> = [
            { text: t("TopicChallenge.Edit.ViewUsersAction"), value: "viewMembers" },
            { text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "delete" }
        ];
        return items;
    }, [viewData]);

    ///
    async function cloneAudience(topicId: string) {
        setProcessing(true);
        try {
            var result = await api.v1.topicChallenge.manageTopicChallenge({ topicId });
            var newAudience = [...viewData!.groups];

            for (const group of result.data.groups) {
                if (!newAudience.some(x => x.id == group.id)) {
                    newAudience.push(group);
                }
            }

            setViewData({ ...viewData!, groups: newAudience });
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setProcessing(false);
        }
    }

    ///
    const handleOptions = (option: AudienceOption) => {
        switch (option) {
            case "cloneAudience":
                setDialog(
                    <TopicSelectorHierarchicalDrawer
                        mode={TopicSelectorMode.Normal}
                        onClose={(topic) => {
                            if (topic && topic.length > 0)
                                cloneAudience(topic[0].topicId);
                            setDialog(null);
                        }}
                    />
                );
                break;

            case "viewAll":
                setDialog(
                    <ListUsersDrawer
                        onClose={() => setDialog(null)}
                        onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                            new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                                api.v1.assManagement.groupsAudienceUsers({
                                    groupsId: viewData!.groups.map(x => x.id),
                                    audiencesNeedAll: viewData?.audiencesNeedAll ?? false,
                                    searchText,
                                    pageNumber,
                                    pageSize
                                }, { signal })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            })
                        }
                    />
                );
                break;
        }
    };

    ///
    const handleOptionsAudience = (option: AudienceItemOption, group: GroupInfo) => {
        switch (option) {
            case "viewMembers":
                setDialog(
                    <ListMemberDrawer
                        subtitle={group.name}
                        onClose={() => setDialog(undefined)}
                        onGetData={({ searchText, pageNumber, pageSize, expanded }, signal) =>
                            new Promise<MemberListDtoPaginatedList>((resolve, reject) => {
                                api.v1.groupManagement.managedGroupGetCurrentMembers({
                                    groupId: group.id,
                                    searchText: searchText,
                                    expanded: expanded,
                                    pageNumber,
                                    pageSize
                                }, { signal })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            })
                        }
                    />
                );
                break;

            case "delete":
                updateRecord("groups", viewData!.groups.filter(p => p.id != group.id));
                break;
        }
    };

    ///
    const handleSelectTab = (event: React.SyntheticEvent, newValue: TopicChallengeTabOptions) => {
        setCurrentTab(newValue);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.topicChallenge.manageTopicChallenge({ topicId });
                setLoadDto(LibUtils.deepCopy(result.data));
                setViewData(result.data);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    // Ao salvar
    useEffect(() => {
        async function postData() {
            try {
                await api.v1.topicChallenge.updateTopicChallenge({
                    topicId,
                    audiencesNeedAll: viewData!.audiencesNeedAll,
                    audience: viewData!.groups.map(x => x.id),
                    disableChallenge: viewData!.disableChallenge,
                    grantSealProvenKnowledge: viewData!.grantSealProvenKnowledge,
                    questionsGroups: viewData!.questionGroups.map(x => x.questionGroupId)
                });
                enqueueSnackbar(t("Record.SaveSuccessMessage", { ns: "shared" }), { variant: "success" });
                onClose(true);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setSaving(false);
            }
        }

        if (saving) postData();

    }, [saving]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const RenderDashboardMessage = (
        <Grid container spacing={2}>
            <Grid>
                <MprcTextMuted>
                    <FontAwesomeIcon icon={faExclamationCircle} size="2x" />
                </MprcTextMuted>
            </Grid>
            <Grid size="grow">
                <Grid container direction="column" spacing={0}>
                    <Grid>
                        <Typography variant="bodyNotice">
                            <MprcTextMuted>
                                <b>{t("TopicChallenge.Edit.ChallengeChangedImpactMessage_L1")}</b>
                            </MprcTextMuted>
                        </Typography>
                    </Grid>
                    <Grid>
                        <Typography variant="bodyNotice">
                            <MprcTextMuted>
                                {t("TopicChallenge.Edit.ChallengeChangedImpactMessage_L2")}
                            </MprcTextMuted>
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );

    ///
    const RenderQuestions = () => {

        if (viewData == null) return null;

        return (
            <EditTopicChallengeQuestions
                viewData={viewData}
                fullScreenMode={fullScreenMode}
                onUpdate={(newViewData) => setViewData(newViewData)}
                refreshQuestionsInfo={(value, changed) => {
                    setQuestionsChanged(changed);
                    setTotalQuestions(value);
                }}
            />
        );
    };

    ///
    const RenderAudience = () => {

        if (viewData == null) return null;

        return (
            <Box width="100%">
                <MprcFilterBox minWitdh="98%">
                    <MprcFilterLine>
                        <AudienceSelectorDropdown
                            text={t("TopicChallenge.Edit.ButtonAddText")}
                            icon={<IconAdd />}
                            onChange={addGroup}
                        />
                        <MprcIconButtonMoreOptionsDropdown
                            items={getMenuItems}
                            onChange={handleOptions}
                        />
                    </MprcFilterLine>
                    <Box sx={{ mt: (theme) => theme.spacing(1) }}>
                        <FormControl>
                            <Typography variant="overline">{t("TopicChallenge.Edit.NeedAllLabel")}:</Typography>
                            <RadioGroup
                                row
                                value={viewData.audiencesNeedAll}
                                onChange={e => updateRecord("audiencesNeedAll", !viewData.audiencesNeedAll)}
                            >
                                <FormControlLabel
                                    disableTypography
                                    value={false}
                                    control={<Radio />}
                                    label={<Typography variant="h6" component="p">{t("TopicChallenge.Edit.Option1")}</Typography>}
                                />
                                <FormControlLabel
                                    disableTypography
                                    value={true}
                                    control={<Radio />}
                                    label={<Typography variant="h6" component="p">{t("TopicChallenge.Edit.Option2")}</Typography>}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </MprcFilterBox>
                <MprcContainer>
                    <Stack direction="column" mt={2} ml={1} minHeight="160px">
                        {
                            viewData.groups.length == 0 &&
                            <Grid size="grow">
                                <Typography variant="h5" component="span">{t("TopicChallenge.Edit.NoAudienceText")}</Typography>
                            </Grid>
                        }
                        {viewData.groups.map((item, index) => {
                            return (
                                <Grid container key={index}>
                                    <Grid size="grow">
                                        <Typography variant="h5" component="span">{item.name}</Typography>
                                    </Grid>
                                    <Grid>
                                        <MprcIconButtonMoreOptionsDropdown
                                            color="muted"
                                            items={getItemMenuOptions}
                                            onChange={(option) => handleOptionsAudience(option, item)}
                                        />
                                    </Grid>
                                </Grid>
                            );
                        })}
                    </Stack>
                </MprcContainer>
            </Box>
        );
    };

    ///
    const RenderOptions = () => {

        if (viewData == null) return null;

        return (
            <>
                <MprcContainer>
                    <Stack direction="column" gap={2} marginTop={1}>
                        <MprcSwitch
                            checked={viewData.disableChallenge}
                            label={t("TopicChallenge.Edit.DisableChallengeLabel")}
                            helperText={t("TopicChallenge.Edit.DisableChallengeHint")}
                            onChange={(event, checked) => updateRecord("disableChallenge", checked)}
                        />
                        <MprcSwitch
                            checked={viewData.grantSealProvenKnowledge}
                            label={
                                <>
                                    {t("TopicChallenge.Edit.GrantSealProvenKnowledgeLabel")}&nbsp;
                                    <MprcText color="info">
                                        <FontAwesomeIcon icon={faShieldQuartered} />
                                    </MprcText>
                                </>
                            }
                            helperText={t("TopicChallenge.Edit.GrantSealProvenKnowledgeHint")}
                            onChange={(event, checked) => updateRecord("grantSealProvenKnowledge", checked)}
                        />
                    </Stack>
                </MprcContainer>

            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}

            <MprcRequestErrorDialog
                error={viewError}
                onClose={() => {
                    setViewError(null);
                    onClose(false);
                }}
            />

            <MprcRequestIndicator type={loading || processing ? "request" : "post"} visible={loading || processing || saving} />

            {
                viewData &&
                <Dialog
                    open
                    fullWidth
                    maxWidth="sm"
                    onClose={() => onClose(false)}
                    fullScreen={fullScreenMode}
                    sx={fullScreenMode ? { "& .MuiPaper-root": { maxHeight: "98%", maxWidth: "70%" } } : undefined}
                >
                    <DialogTitle textTransform="none">
                        <Grid container justifyContent="space-between">
                            <Grid>
                                <Typography variant="h4">{t("TopicChallenge.Edit.Title")}</Typography>
                                <Typography noWrap={true} variant="caption">{viewData?.fullName ?? ""}</Typography>
                            </Grid>
                            <Grid>
                                <IconButtonExpandScreen
                                    expanded={fullScreenMode}
                                    size="small"
                                    onClick={() => setFullScreenMode(!fullScreenMode)}
                                />
                                <IconButtonClose
                                    color="inherit"
                                    size="small"
                                    onClick={() => onClose(false)}
                                />
                            </Grid>

                        </Grid>
                        {
                            showChangedImpact &&
                            <Box my={1}>
                                <MprcMessageBox
                                    variant="notice"
                                    fullWidth
                                    message={RenderDashboardMessage}
                                />
                            </Box>
                        }
                        <MprcBarTabs
                            variant="fullWidth"
                            value={currentTab}
                            onChange={handleSelectTab}>
                            <Tab value={"questions"} label={t("TopicChallenge.Edit.QuestionsTabText")} />)
                            <Tab value={"audience"} label={t("TopicChallenge.Edit.AudienceTabText")} />)
                            <Tab value={"options"} label={t("TopicChallenge.Edit.OptionsTabText")} />)
                        </MprcBarTabs>
                    </DialogTitle>
                    <DialogContent sx={{ height: fullScreenMode ? "50%" : "400px", paddingBottom: "4px" }}>
                        {
                            currentTab == "questions"
                                ? RenderQuestions()
                                : currentTab == "audience"
                                    ? RenderAudience()
                                    : RenderOptions()
                        }
                    </DialogContent>

                    <DialogActions>
                        <Grid size="grow">
                            {
                                currentTab == "questions" &&
                                <Typography variant="subtitle1" textTransform="uppercase" marginLeft={2}>
                                    <MprcTextMuted variant="main">
                                        {t("TopicChallenge.Edit.TotalQuestionText", { count: totalQuestions })}
                                    </MprcTextMuted>
                                </Typography>
                            }
                        </Grid>
                        <Grid>
                            <Grid container spacing={1}>
                                <Grid>
                                    <ButtonCancel
                                        variant="contained"
                                        onClick={() => onClose(false)}
                                    />
                                </Grid>
                                <Grid>
                                    <ButtonSave
                                        disabled={!canSave}
                                        variant="contained"
                                        onClick={() => setSaving(true)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </Dialog>
            }
        </>
    );
};
